import { useMemo } from 'react';
import { describeSeat, manifestTree } from './list';
import type { AvailabilityMap, GaAreaGeometry, ManifestGeometry, SeatGeometry } from './types';

/**
 * The same seats as the canvas, as a table (spec §67). Every seat is a real
 * button with a full description, so a keyboard or screen-reader user can pick
 * A-12 without a pointer, and an operator can find a seat by number without
 * hunting for it on the drawing.
 */
export interface SeatListProps {
  layout: ManifestGeometry;
  availability?: AvailabilityMap;
  selectedSeatIds?: ReadonlySet<string>;
  onToggleSeat?: (seat: SeatGeometry) => void;
  /** Hide seats that are not selectable (view mode with availability). */
  hideUnavailable?: boolean;
  /**
   * General admission (D-184). The list is how somebody buys without a pointer,
   * and it showed no GA at all — so on a general-admission night it was an
   * empty page, and on a mixed one it quietly left out half the room.
   */
  gaAvailable?: ReadonlyMap<string, number> | undefined;
  gaInCart?: ReadonlyMap<string, number> | undefined;
  onGaAreaClick?: ((area: GaAreaGeometry, at: { x: number; y: number } | null) => void) | undefined;
  className?: string;
}

const flags = (seat: SeatGeometry): string[] => {
  const out: string[] = [];
  if (seat.accessible) out.push('♿');
  if (seat.companion) out.push('companion');
  if (seat.restricted_view) out.push('restricted view');
  if (seat.obstructed_view) out.push('obstructed');
  if (!seat.active) out.push('inactive');
  return out;
};

export function SeatList({ layout, availability, selectedSeatIds, onToggleSeat, hideUnavailable, gaAvailable, gaInCart, onGaAreaClick, className }: SeatListProps) {
  const tree = useMemo(() => manifestTree(layout), [layout]);

  return (
    <div className={className}>
      {tree.map(({ section, level_name, groups, seat_count }) => (
        <details key={section.id} open={tree.length <= 3}>
          <summary style={{ cursor: 'pointer', padding: '6px 0', fontWeight: 600 }}>
            {section.name}
            <span style={{ fontWeight: 400, color: '#7b8396', marginLeft: 8, fontSize: 12 }}>
              {level_name ? `${level_name} · ` : ''}
              {seat_count} seat{seat_count === 1 ? '' : 's'}
            </span>
          </summary>
          {groups.length === 0 ? (
            <p style={{ fontSize: 13, color: '#7b8396', margin: '4px 0 8px' }}>No numbered seats in this section.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <caption style={{ position: 'absolute', left: -9999 }}>Seats in {section.name}</caption>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id} style={{ borderBottom: '1px solid #e3e6ec', verticalAlign: 'top' }}>
                    <th scope="row" style={{ textAlign: 'left', padding: '4px 8px 4px 0', whiteSpace: 'nowrap', width: 90, fontWeight: 600 }}>
                      {group.kind === 'row' ? `Row ${group.label}` : `Table ${group.label}`}
                    </th>
                    <td style={{ padding: '3px 0' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {group.seats.map((seat) => {
                          const state = availability?.[seat.id];
                          const disabled = Boolean(availability) && state !== 'AVAILABLE' && state !== 'HELD_BY_YOU' && state !== 'ACCESSIBLE_ON_REQUEST';
                          if (hideUnavailable && disabled) return null;
                          const selected = selectedSeatIds?.has(seat.id) ?? false;
                          const extras = flags(seat);
                          return (
                            <button
                              key={seat.id}
                              type="button"
                              aria-pressed={selected}
                              aria-label={describeSeat(seat, group, section) + (state ? `, ${state.toLowerCase().replaceAll('_', ' ')}` : '')}
                              disabled={disabled || !onToggleSeat}
                              onClick={() => onToggleSeat?.(seat)}
                              title={extras.join(', ') || undefined}
                              style={{
                                minWidth: 30,
                                padding: '2px 6px',
                                borderRadius: 3,
                                border: `1px solid ${selected ? '#171a21' : seat.accessible || seat.companion ? '#2f6fb3' : '#e3e6ec'}`,
                                background: selected ? '#171a21' : state === 'HELD_BY_YOU' ? '#fbf0d8' : disabled ? '#f7f8fa' : '#fff',
                                color: selected ? '#fff' : disabled ? '#7b8396' : '#171a21',
                                cursor: disabled || !onToggleSeat ? 'default' : 'pointer',
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: 12,
                              }}
                            >
                              {seat.seat_number}
                              {/* The same two marks the map draws (D-91). */}
                              {seat.accessible ? ' ♿' : seat.companion ? ' C' : ''}
                              {seat.restricted_view || seat.obstructed_view ? ' *' : ''}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </details>
      ))}

      {/*
        General admission, said in words (D-184). A GA area has no seat numbers
        to list, so it is one row with what it costs, what is left and what this
        customer already holds — the same facts the shape on the map carries.
      */}
      {layout.ga_areas.length > 0 ? (
        <details open>
          <summary style={{ cursor: 'pointer', padding: '6px 0', fontWeight: 600 }}>
            General admission
            <span style={{ fontWeight: 400, color: '#7b8396', marginLeft: 8, fontSize: 12 }}>
              {layout.ga_areas.length} area{layout.ga_areas.length === 1 ? '' : 's'}
            </span>
          </summary>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <caption style={{ position: 'absolute', left: -9999 }}>General admission areas</caption>
            <tbody>
              {layout.ga_areas.map((area) => {
                const left = gaAvailable?.get(area.id);
                const mine = gaInCart?.get(area.id) ?? 0;
                const soldOut = left === 0;
                const detail = soldOut ? 'Sold out' : left === undefined ? '' : `${left} left`;
                return (
                  <tr key={area.id} style={{ borderBottom: '1px solid #e3e6ec', verticalAlign: 'top' }}>
                    <th scope="row" style={{ textAlign: 'left', padding: '4px 8px 4px 0', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {area.name}
                    </th>
                    <td style={{ padding: '3px 0', color: '#7b8396' }}>
                      {detail}
                      {mine > 0 ? `${detail ? ' · ' : ''}${mine} in your order` : ''}
                    </td>
                    <td style={{ padding: '3px 0', textAlign: 'right' }}>
                      <button
                        type="button"
                        disabled={soldOut || !onGaAreaClick}
                        onClick={() => onGaAreaClick?.(area, null)}
                        aria-label={`Add a ticket for ${area.name}${soldOut ? ', sold out' : left === undefined ? '' : `, ${left} left`}`}
                        style={{
                          padding: '2px 10px',
                          borderRadius: 3,
                          border: '1px solid #e3e6ec',
                          background: soldOut ? '#f7f8fa' : '#fff',
                          color: soldOut ? '#7b8396' : '#171a21',
                          cursor: soldOut || !onGaAreaClick ? 'default' : 'pointer',
                          fontSize: 12,
                        }}
                      >
                        {soldOut ? 'Sold out' : 'Add'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}
